interface NowPlayingIndicatorProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  paused?: boolean;
}

const sizeConfig = {
  sm: { barWidth: 2, barGap: 1, heights: [8, 12, 6, 10], containerWidth: 14 },
  md: { barWidth: 3, barGap: 1.5, heights: [12, 18, 10, 16], containerWidth: 20 },
  lg: { barWidth: 4, barGap: 2, heights: [16, 24, 12, 20], containerWidth: 28 },
};

/**
 * Animated sound wave bars indicator for currently playing track
 * Shows 4 bars with staggered animation for a realistic audio visualization effect
 */
export function NowPlayingIndicator({
  size = 'sm',
  className = '',
  paused = false,
}: NowPlayingIndicatorProps) {
  const config = sizeConfig[size];
  const barCount = 4;
  const delays = [0, 0.2, 0.4, 0.1]; // Staggered animation delays

  return (
    <div
      className={`flex items-end justify-center ${className}`}
      style={{
        width: config.containerWidth,
        height: config.heights[1], // Use tallest bar height
        gap: config.barGap,
      }}
      role="img"
      aria-label={paused ? 'Audio paused' : 'Audio playing'}
    >
      {Array.from({ length: barCount }).map((_, index) => (
        <div
          key={`bar-${index}`}
          className="bg-[#8e4585] rounded-full"
          style={{
            width: config.barWidth,
            height: paused ? config.heights[index] * 0.3 : config.heights[index],
            animationName: paused ? 'none' : 'soundWave',
            animationDuration: '0.8s',
            animationTimingFunction: 'ease-in-out',
            animationIterationCount: 'infinite',
            animationDelay: `${delays[index]}s`,
            transition: 'height 0.2s ease-out',
          }}
        />
      ))}

      {/* Inline keyframes for sound wave animation */}
      <style>{`
        @keyframes soundWave {
          0%, 100% {
            height: 25%;
          }
          50% {
            height: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default NowPlayingIndicator;
